import { type ReactNode, useEffect, useState } from "react";
import { cn } from "@repo/utils";
import NavIconButton from "./nav-icon-button.js";

type Props = {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
  toggleState?: boolean;
  showChevron?: boolean;
  showBorderLine?: boolean;
  className?: string;
};

export function NavSection({
  title,
  children,
  defaultOpen = true,
  toggleState,
  showChevron = true,
  showBorderLine = true,
  className,
}: Props) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  useEffect(() => {
    if (toggleState !== undefined) {
      setIsOpen(toggleState);
    }
  }, [toggleState]);

  const toggleOpen = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex flex-row items-center justify-between">
        <div className="text-sm font-semibold">{title}</div>
        {title == "Navigation" && (
          <div
            role="button"
            tabIndex={-1}
            data-allow-navigation
            onClick={() => setIsOpen(!isOpen)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setIsOpen(!isOpen);
              }
            }}
          >
            <NavIconButton
              text={""}
              isOpen={isOpen}
              showChevron={showChevron}
              data-allow-navigation
              aria-label={isOpen ? "Collapse section" : "Expand section"}
              customButtonClassName="h-fit w-fit"
              chevronPosition="180"
              toggleOpen={toggleOpen}
            />
          </div>
        )}
      </div>

      {isOpen && (
        <>
          <div className="space-y-4">{children}</div>
          {showBorderLine && <div className="my-4 border-t border-gray-200" />}
        </>
      )}
    </div>
  );
}
